/**
 * Shared format / date helpers used across pages.
 *
 * Покриті vitest-тестами в `format.test.ts`. Усі сторінки повинні імпортувати
 * звідси, щоб не дублювати реалізацію.
 */

/** Безпечно нормалізує `number | string | null` → `number | null`. */
export function num(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

/** Локалізоване форматування числа. `null` → «—». */
export function fmt(n: number | null, digits = 0): string {
  if (n === null) return "—";
  return n.toLocaleString("uk-UA", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

/** YYYY-MM-DD сьогодні (UTC midnight локального дня). */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** YYYY-MM-DD через `daysAgo` днів назад від сьогодні. */
export function daysAgoIso(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

/** Locale-незалежна локальна година Kyiv: «00».."24" (24 для DST autumn duplicate). */
export function hourFromTs(ts: string): number {
  const d = new Date(ts);
  const h = d.toLocaleString("uk-UA", {
    hour: "2-digit",
    timeZone: "Europe/Kyiv",
    hour12: false,
  });
  return Number(h.replace(/[^\d]/g, ""));
}

/** "HH:00" з 0..24. */
export function fmtHourLocal(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

/** Зсунута мітка години для таблиць/графіків: 0→1, 1→2, …, 22→23, 23→0.
 * Бізнес-нумерація годин замовника. */
export function hourLabelShifted(hour: number): string {
  return String((hour + 1) % 24);
}

export type Period = "week" | "month" | "quarter" | "year";

/** День-діапазон для PeriodSwitcher. `end` inclusive. */
export function rangeFor(end: string, period: Period): { from: string; to: string } {
  const to = new Date(end);
  const from = new Date(to);
  const offset = period === "week" ? 6 : period === "month" ? 29 : period === "quarter" ? 89 : 364;
  from.setDate(to.getDate() - offset);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

const _isoOf = (d: Date): string => d.toISOString().slice(0, 10);

/** Календарний діапазон періоду, що МІСТИТЬ дату `dateIso` (inclusive).
 * week — Пн–Нд (ISO); month/quarter/year — календарні. */
export function calendarRangeFor(dateIso: string, period: Period): { from: string; to: string } {
  const d = new Date(dateIso + "T00:00:00Z");
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  if (period === "week") {
    const dow = (d.getUTCDay() + 6) % 7; // 0 = понеділок
    const from = new Date(d);
    from.setUTCDate(d.getUTCDate() - dow);
    const to = new Date(from);
    to.setUTCDate(from.getUTCDate() + 6);
    return { from: _isoOf(from), to: _isoOf(to) };
  }
  if (period === "month") {
    return { from: _isoOf(new Date(Date.UTC(y, m, 1))), to: _isoOf(new Date(Date.UTC(y, m + 1, 0))) };
  }
  if (period === "quarter") {
    const qs = Math.floor(m / 3) * 3;
    return { from: _isoOf(new Date(Date.UTC(y, qs, 1))), to: _isoOf(new Date(Date.UTC(y, qs + 3, 0))) };
  }
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

/** Зсув опорної дати на ±1 період (для стрілок ‹ ›). */
export function shiftPeriod(dateIso: string, period: Period, dir: -1 | 1): string {
  const d = new Date(dateIso + "T00:00:00Z");
  if (period === "week") d.setUTCDate(d.getUTCDate() + 7 * dir);
  else if (period === "month") d.setUTCMonth(d.getUTCMonth() + dir, 1);
  else if (period === "quarter") d.setUTCMonth(d.getUTCMonth() + 3 * dir, 1);
  else d.setUTCFullYear(d.getUTCFullYear() + dir, 0, 1);
  return _isoOf(d);
}

/** N-day range з кінцем `end` (inclusive). days=1 → [end..end]. */
export function rangeDaysBefore(end: string, days: number): { from: string; to: string } {
  const to = new Date(end);
  const from = new Date(to);
  from.setDate(to.getDate() - (days - 1));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}
